<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="6" />
      <vs-col vs-w="2">
        <div class="flex items-center">
          <select-with-search
            hide-search
            :clearable="false"
            v-model="filters.year"
            placeholder="Select Year"
            class="px-2"
            :options="yearOptions"
          />
        </div>
      </vs-col>
      <vs-col vs-w="2">
        <select-with-search
          hide-search
          v-model="filters.shippers"
          class="px-2"
          :options="shipperOptions"
          placeholder="Select Shippers"
        />
      </vs-col>
      <vs-col vs-w="1">
        <div class="flex items-center pl-2">
          <vs-button
            type="border"
            color="primary"
            class="px-2 w-40 self-center font-semibold"
            @click="resetFilters"
          >
            Reset
          </vs-button>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5" 
        @fetch="fetchSTA()"
        ref="tableTop"
      >
        <template #start>
          <vs-button
            class="button shadow-secondary font-semibold"
            color="primary"
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            @click="addNewSTA"
          >
            Add New STA
          </vs-button>
          <vs-button
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            @click="showDuplicatePopup"
          >
            Duplicate STA
          </vs-button>
          <vs-button
            v-show="true"
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
            :disabled="isDownload"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :table-header="tableHeaders"
        :table-data="standardTimeData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :frozen-columns="isActionColumnVisible ? 5 : 4"
        :header-height="'60px'"
        :loading="isBusy" 
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #hp_id="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <select-with-search
                :disabled="isProcessing"
                v-model="row.editForm.hp_id.value"
                :class="{ 'is-not-valid': !row.editForm.hp_id.isValid }"
                :options="horsePowersOptions"
                placeholder="Select horsepower"
              />
              <span
                v-if="!row.editForm.hp_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.horse_power.name ? row.horse_power.name.toLocaleString('id-ID') : '-' }}
          </template>
        </template>
        <template #shipper="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <select-with-search
                :disabled="isProcessing"
                v-model="row.editForm.shipper.value"
                :class="{ 'is-not-valid': !row.editForm.shipper.isValid }"
                :options="shipperOptions"
                placeholder="Select shipper"
              />
              <span
                v-if="!row.editForm.shipper.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.shipper ? row.shipper.toLocaleString('id-ID') : '-' }}
          </template>
        </template>
        <template #route_name="{ row }">
          <div
            class="p-2"
            v-if="row.isEditing"
            style="min-width: 200px"
          >
            <select-with-multiple
              :disabled="isProcessing"
              v-model="row.editForm.sta_route_details"
              class="px-2"
              :options="routeOptions"
              placeholder="Select Route"
              :activate-search="true"
              :is-can-add="true"
              :on-add="handleAdd"
              :is-add-one-by-one="true"
              separator="-"
              :allow-duplicate-values="true"
              :danger="!row.editForm.sta_route_details.length"
              danger-text="* Required"
            />
          </div>
          <div v-else>
            {{ row.route_name || '-' }}
          </div>
        </template>
        <template
          v-for="n in staColumns"
          #[`${n.name}_${n.oee_id}`]="{ row }"
        >
          <div
            :key="n.text"
          >
            <div
              v-if="row.isEditing"
              class="relative p-2"
            >
              <vs-input
                type="text"
                :disabled="isProcessing"
                :danger="getStaValue(row, n.name, n.oee_id) === ''"
                :danger-text="'* Required'"
                :value="getStaValue(row, n.name, n.oee_id)"
                @input="updateStaValue(row, n.name, n.oee_id, $event)"
              />
            </div>
            <div
              v-else
              class="flex flex-col items-center w-full"
            >
              <div>
                {{ getStaValue(row, n.name, n.oee_id) }}
              </div>
            </div>
          </div>
        </template>
        <template #total_operating_time="{ row }">
          <div class="flex flex-col items-center w-full">
            {{
              (row.total_operating_time === undefined || row.total_operating_time === null)
                ? '0'
                : (row.total_operating_time === 0
                  ? '0'
                  : (row.total_operating_time % 1 === 0
                    ? Math.floor(row.total_operating_time)
                    : row.total_operating_time.toFixed(2)))
            }}
          </div>
        </template>
        <template #total_delay_time="{ row }">
          <div class="flex flex-col items-center w-full">
            {{
              (row.total_delay_time === undefined || row.total_delay_time === null)
                ? '0'
                : (row.total_delay_time === 0
                  ? '0'
                  : (row.total_delay_time % 1 === 0
                    ? Math.floor(row.total_delay_time)
                    : row.total_delay_time.toFixed(2)))
            }}
          </div>
        </template>
        <template #total_downtime_time="{ row }">
          <div class="flex flex-col items-center w-full">
            {{
              (row.total_downtime_time === undefined || row.total_downtime_time === null)
                ? '0'
                : (row.total_downtime_time === 0
                  ? '0'
                  : (row.total_downtime_time % 1 === 0
                    ? Math.floor(row.total_downtime_time)
                    : row.total_downtime_time.toFixed(2)))
            }}
          </div>
        </template>
        <template #total_barge_cycle="{ row }">
          <div class="flex flex-col items-center w-full">
            {{
              (row.total_barge_cycle === undefined || row.total_barge_cycle === null)
                ? '0'
                : (row.total_barge_cycle === 0
                  ? '0'
                  : (row.total_barge_cycle % 1 === 0
                    ? Math.floor(row.total_barge_cycle)
                    : row.total_barge_cycle.toFixed(2)))
            }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-check-circle.svg')">
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-close-circle.svg')">
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startDelete(row)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
      </data-table>
      <table-bottom @fetch="fetchSTA()" />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete STA"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, isShownRestore: false, tempId: null, tempName: '' }"
      @rightButton="deleteSTA()"
    >
      <p class="text-center mt-1">
        Are you sure want to delete STA for
      </p>
      <p class="text-center">
        "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUp.isRestoreShown"
      is-warning
      title="Restore STA"
      left-button="Cancel"
      right-button="Restore"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreSTA"
    >
      <p class="text-center mt-1">
        {{ `Standard Time for "${restoreData.hp_id} ${restoreData.route_name} ${restoreData.shipper}" already exists but has been deleted.` }}
      </p>
      <p class="text-center">
        Would you like to restore it?
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
    >
      You have successfully {{ popUpSuccessAction }} STA
    </pop-up>

    <pop-up
      :active.sync="duplicatePopup.isShowChoose"
      title="Duplicate Data"
      is-warning
      left-button="Cancel"
      right-button="Select Year"
      @close="handleDuplicateClose"
      @leftButton="handleDuplicateClose"
      @rightButton="showDuplicateConfirmation"
    >
      <div 
        class="flex flex-col gap-3 mt-5" 
        style="min-width: 250px;"
      >
        <div class="flex flex-col gap-2">
          <label class="font-medium text-black">Data Source</label>
          <div class="relative">
            <select-with-search
              v-model="duplicatePopup.sourceYear"
              :options="yearOptions"
              placeholder="Select Year"
              hide-search
            />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label class="font-medium text-black">Duplicate To</label>
          <select-with-search
            v-model="duplicatePopup.targetYear"
            :options="targetYearOptions"
            placeholder="Select Year"
          />
        </div>
      </div>
    </pop-up>
    <pop-up
      :active.sync="duplicatePopup.isShowConfirm"
      title="Duplicate Data"
      is-warning
      left-button="Cancel"
      right-button="Duplicate Data"
      @close="handleDuplicateClose"
      @leftButton="handleDuplicateClose"
      @rightButton="handleDuplicate"
      :is-busy="isProcessing"
    >
      <p class="text-center mt-1">
        Are you sure you want to duplicate STA data
      </p>
      <p class="text-center">
        from {{ duplicatePopup.sourceYear }} to {{ duplicatePopup.targetYear }}
      </p>
    </pop-up>
  </div>
</template>
  
<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithMultiple from '@/components/selectWithMultiple'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'
  
export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    TableBottom,
    TableTop,
    DataTable,
    SelectWithSearch,
    PopUp,
    SelectWithMultiple
  },
  data() {
    return {
      isBusy: false,
      isAdding: false,
      isDownload: false,
      popUp: {
        isShown: false,
        isRestoreShown: false,
        tempId: null,
        tempName: ''
      },
      restoreData: {
        id: null,
        hp_id: null,
        route_name: null,
        shipper: null,
        formData: {}
      },
      popUpSuccess: false,
      popUpSuccessAction: 'deleted',
      shipperOptions: [],
      horsePowersOptions: [],
      routeOptions: [],
      standardTimeData: [],
      yearOptions: [],
      filters: {
        sort_dir: '',
        sort_by: '',
        year: this.years?.[0] || '',
        shippers: ''
      },
      tableHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '50px' },
        { key: 'hp_id', label: 'HP', sortable: true },
        { key: 'shipper', label: 'Shipper', sortable: true },
        { key: 'route_name', label: 'Route', sortable: true }
      ],
      operationColumns: [],
      delayColumns: [],
      downtimeColumns: [],
      bargeCycle: [],
      isProcessing: false,
      duplicatePopup: {
        isShowChoose: false,
        isShowConfirm: false,
        sourceYear: this.filters?.year || '',
        targetYear: ''
      }
    }
  },
  computed: {
    ...mapGetters({
      totalPage: 'Table/totalPage',
      totalData: 'Table/totalData',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      staColumns: 'StandardTimeAnalysis/fetchColumns',
      standardTimeList: 'StandardTimeAnalysis/staList',
      standardTimeDetail: 'StandardTimeAnalysis/staDetail',
      shippers: 'StandardTimeAnalysis/shippers',
      years: 'StandardTimeAnalysis/years',
      horsePowers: 'TugManagement/horsePowers',
      routes: 'FuelSupplyTugboat/routes',
      staFiles: 'StandardTimeAnalysis/staFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    },
    targetYearOptions() {
      const currentYear = new Date().getFullYear();
      const existingYears = new Set(this.yearOptions.map(opt => Number(opt.value)));
      const startYear = currentYear - 100;
      const endYear = currentYear + 100;
      
      return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)
        .filter(year => !existingYears.has(year))
        .map(year => ({
          text: year.toString(),
          value: year
        }));
    }
  },
  watch: {
    standardTimeList: {
      handler(val) {
        if (!val || !val.length) {
          this.standardTimeData = [];
          return;
        }
        this.standardTimeData = val.map(item => {
          const baseData = {
            id: item.id,
            year: item.year,
            hp_id: item.horse_power?.id,
            horse_power: item.horse_power,
            shipper: item.shipper,
            route_name: item.route_name,
            sta_route_details: item.sta_route_details.map(route => ({
              location_name: route.location_name,
              sequence_order: route.sequence_order,
              value: route.location_name,
              text: route.location_name
            })),
            sta_details: item.sta_details.map(details => ({
              id: details.id,
              name: details.name,
              oee_id: details.oee_id || this.staColumns.find(s => s.name === details.name)?.oee_id,
              value: details.value
            })),
            isEditing: false,
            editForm: null
          }

          const totalTime = item.sta_details
            .filter(detail => this.operationColumns.includes(detail.oee_id))
            .reduce((sum, detail) => sum + (detail.value || 0), 0)

          const totalDelay = item.sta_details
            .filter(detail => this.delayColumns.includes(detail.oee_id))
            .reduce((sum, detail) => sum + (detail.value || 0), 0)

          const totalDowntime = item.sta_details
            .filter(detail => this.downtimeColumns.includes(detail.oee_id))
            .reduce((sum, detail) => sum + (detail.value || 0), 0)

          const totalDelayTime = Number((totalDelay / 24).toFixed(2))
          const totalDowntimeTime = Number((totalDowntime / 24).toFixed(2))
          const totalOperatingTime = Number((totalTime / 24).toFixed(2))
          const totalBargeCycle = Number((totalOperatingTime + totalDelayTime + totalDowntimeTime).toFixed(2))

          return {
            ...baseData,
            total_operating_time: totalOperatingTime,
            total_delay_time: totalDelayTime,
            total_downtime_time: totalDowntimeTime,
            total_barge_cycle: totalBargeCycle
          }
        })
      },
      immediate: true
    },
    staColumns: {
      handler(val) {
        if (val) {
          const baseHeaders = [
            { key: 'no', label: 'No', sortable: false, width: '50px' }
          ];
          
          if (this.isActionColumnVisible) {
            baseHeaders.push({ key: 'action', label: 'Action', sortable: false, width: '75px' });
          }

          baseHeaders.push(
            { key: 'hp_id', label: 'HP', sortable: true },
            { key: 'shipper', label: 'Shipper', sortable: true },
            { key: 'route_name', label: 'Route', sortable: true }
          );

          this.tableHeaders = baseHeaders;

          this.operationColumns = val
            .filter(col => _.upperCase(col.type) === 'OPERATION')
            .map(col => col.oee_id)

          this.delayColumns = val
            .filter(col => _.upperCase(col.type) === 'DELAY')
            .map(col => col.oee_id)

          this.downtimeColumns = val
            .filter(col => _.upperCase(col.type) === 'DOWNTIME')
            .map(col => col.oee_id)

          const dynamicColumns = val.map(v => ({
            key: `${v.name}_${v.oee_id}`,
            label: `${v.text}<br>(${v.unit})`
          }));
          this.tableHeaders.push(...dynamicColumns);

          if (!this.tableHeaders.find(header => header.key === 'total_delay_time')) {
            this.tableHeaders.splice(this.tableHeaders.length, 0, {
              key: 'total_delay_time',
              label: 'Total Delay Time <br>by Charterer(Days)'
            })
          }
          if (!this.tableHeaders.find(header => header.key === 'total_downtime_time')) {
            this.tableHeaders.splice(this.tableHeaders.length, 0, {
              key: 'total_downtime_time',
              label: 'Total Delay Time <br>by Barge Owner(Days)'
            })
          }
          if (!this.tableHeaders.find(header => header.key === 'total_operating_time')) {
            this.tableHeaders.splice(this.tableHeaders.length, 0, {
              key: 'total_operating_time',
              label: 'Total Operating Time<br>(Days)'
            })
          }
          if (!this.tableHeaders.find(header => header.key === 'total_barge_cycle')) {
            this.tableHeaders.splice(this.tableHeaders.length, 0, {
              key: 'total_barge_cycle',
              label: 'Barge Cycle Time<br>(Days)'
            })
          }
        }
      },
      immediate: true
    },
    shippers: {
      handler(val) {
        if (val) {
          this.shipperOptions = val.map(item => ({ text: item.name, value: item.name }))
        }
      }
    },
    filters: {
      handler() {
        this.fetchSTA();
      },
      deep: true
    },
    years: {
      handler(val) {
        if (val && val.length) {
          this.yearOptions = val.map(item => ({ text: item, value: item }));
          if (!this.filters.year) {
            this.filters.year = new Date().getFullYear();
          }
        }
      },
      immediate: true
    },
    horsePowers: {
      handler(val) {
        if (val) {
          this.horsePowersOptions = val.map(item => ({ text: item.horse_power, value: item.horse_power_id }))
        }
      }
    },
    routes: {
      handler(val) {
        if (val) {
          this.routeOptions = val
            .filter(item => item.type_port === 'POL' || item.type_port === 'POD' && item.name)
            .map(item => ({ text: item.name, value: item.name }))
        }
      }
    },
    'row.editForm.sta_details': {
      handler(val) {
        if (val) {
          this.recalculateTotals(row);
        }
      },
      deep: true
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadSTA()
        }
      }
    },
    staFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Standard-Time-Analysis-${this.filters.year}.xlsx`)
          this.$store.dispatch('StandardTimeAnalysis/resetSTAFiles');
        }
      }
    },
    'filters.year': {
      handler(val) {
        this.duplicatePopup.sourceYear = val || '';
      },
      immediate: true
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.tableHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.tableHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.tableHeaders.splice(index, 1);
        }
      }
    }
  },
  async mounted() {
    this.fetchSTA()
    this.fetchColumns()
    this.fetchShippers()
    this.fetchYear()
    this.fetchHorsePowers()
    this.fetchRoutes()
  },
  methods: {
    async fetchColumns() {
      try {
        await this.$store.dispatch('StandardTimeAnalysis/fetchColumns')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch STA columns, please try again')
      }
    },
    async fetchSTA() {
      try {
        this.isBusy = true
        const apiFilters = {
          sort_dir: (this.filters?.sort_dir || 'asc').toUpperCase(),
          sort_by: this.filters?.sort_by || 'route_name',
          page: this.currentPage || 1,
          limit: this.pageSize || 10,
          is_get_deleted: false,
          year: this.filters?.year || this.years[0],
          shipper: this.filters?.shippers || ''
        };

        await this.$store.dispatch('StandardTimeAnalysis/fetchSTAList', apiFilters)

        this.isAdding = false
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch STA list, please try again')
      }
    },
    async fetchShippers() {
      try {
        await this.$store.dispatch('StandardTimeAnalysis/fetchShippers')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch shipper, please try again')
      }
    },
    async fetchYear() {
      try {
        await this.$store.dispatch('StandardTimeAnalysis/fetchYear')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch year, please try again')
      }
    },
    async fetchHorsePowers() {
      try {
        await this.$store.dispatch('TugManagement/fetchHorsePowers')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Horse Powers, please try again')
      }
    },
    async fetchRoutes() {
      try {
        await this.$store.dispatch('FuelSupplyTugboat/fetchRoutes')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch routes, please try again')
      }
    },
    addNewSTA() {
      if (this.standardTimeData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      const staDetails = this.staColumns.map(col => ({
        name: col.name,
        oee_id: col.oee_id,
        value: 0
      }));

      const newRow = {
        id: `new-${Date.now()}`,
        isEditing: true,
        active: true,
        editForm: {
          hp_id: { value: '', isValid: true },
          route_name: { value: '', isValid: true},
          shipper: { value: '', isValid: true},
          sta_route_details: [],
          sta_details: [...staDetails]
        }
      };

      this.isAdding = true;
      this.standardTimeData.unshift(newRow);
    },
    startDelete(row) {
      if (this.isAdding || this.standardTimeData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }
      this.popUp = { isShown: true, tempId: row.id, tempName: `${row.shipper} ${row.horse_power?.name} ${row.route_name}` }
    },
    startEdit(row) {
      if (this.standardTimeData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      const index = this.standardTimeData.findIndex(item => item.id === row.id);
      if (index !== -1) {
        this.$set(this.standardTimeData[index], 'isEditing', true);
        this.$set(this.standardTimeData[index], 'editForm', {
          hp_id: { value: row.hp_id, isValid: true },
          route_name: { value: row.route_name, isValid: true},
          shipper: { value: row.shipper, isValid: true},
          sta_route_details: row.sta_route_details.map((detail, index) => ({
            location_name: detail.location_name,
            sequence_order: index,
            value: detail.location_name,
            text: detail.location_name
          })),
          sta_details: row.sta_details.map(details => ({
            id: details.id,
            name: details.name,
            oee_id: details.oee_id,
            value: details.value
          }))
        });
      }
    },
    cancelEdit(row) {
      if (this.isAdding) {
        const index = this.standardTimeData.findIndex(item => item.id === row.id);
        if (index !== -1) {
          this.standardTimeData.splice(index, 1);
        }
        this.isAdding = false;
      } else {
        const index = this.standardTimeData.findIndex(item => item.id === row.id);
        if (index !== -1) {
          this.$set(this.standardTimeData[index], 'isEditing', false);
          this.$set(this.standardTimeData[index], 'editForm', null);
        }
      }
      this.isAdding = false;
    },
    cancelAllEdits() {
      if (this.isAdding) {
        const index = this.standardTimeData.findIndex(row => row.isEditing);
        if (index !== -1) {
          this.standardTimeData.splice(index, 1);
        }
        this.isAdding = false;
      }
      
      this.standardTimeData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false);
          this.$set(row, 'editForm', null);
        }
      });
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },

    async downloadSTA() {
      try {
        this.isBusy = true
        await this.$store.dispatch('StandardTimeAnalysis/downloadSTA', {
          year: this.filters?.year || this.years[0],
          shipper: this.filters.shippers || ''
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false
      } catch (error) {
        this.isDownload = false
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to download Standard Time, please try again')
      }
    },
    resetFilters() {
      this.filters = {
        sort_dir: '',
        sort_by: '',
        year: this.years?.[0] || '',
        shippers: ''
      }
      this.$refs.tableTop.$emit('reset-search')
      this.fetchSTA()

    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchSTA();
    },
    handleAdd(searchTerm) {
      const newOption = { value: searchTerm, text: searchTerm };
      this.routeOptions.push(newOption);
      
      const editingRow = this.standardTimeData.find(row => row.isEditing);
      if (editingRow && editingRow.editForm) {
        const newRoute = {
          location_name: searchTerm,
          sequence_order: editingRow.editForm.sta_route_details.length + 1,
          value: searchTerm,
          text: searchTerm
        };
        editingRow.editForm.sta_route_details.push(newRoute);
      }
    },
    async deleteSTA() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('StandardTimeAnalysis/deleteSTA', this.popUp.tempId)
        this.isProcessing = false;
        this.popUp = { isShown: false, tempId: null }
        this.popUpSuccess = true
        await this.fetchSTA();
      } catch (error) {
        this.isProcessing = false;
        this.showFailedNotification(this, error?.message || error?.error || 'Failed to delete STA, please try again');
      }
    },
    async checkRestoreData(formData) {
      try {
        await this.$store.dispatch('StandardTimeAnalysis/fetchSTAList', {
          hp_id: formData.hp_id,
          route_name: formData.route_name,
          shipper: formData.shipper,
          year: formData.year,
          is_get_deleted: true
        });
        if (this.standardTimeDetail && this.standardTimeDetail.id) {
          const { id, hp_id, route_name, shipper } = this.standardTimeDetail
          this.restoreData.id = id;
          this.restoreData.hp_id = hp_id;
          this.restoreData.route_name = route_name;
          this.restoreData.shipper = shipper;
          this.popUp.isRestoreShown = true;
          this.restoreData.formData = formData;
          return true;
        }
        return false;
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to check existing data');
      }
    },
    closeRestoreModal() {
      this.popUp.isRestoreShown = false;
      this.restoreData = {
        id: null,
        hp_name: null,
        route_name: null,
        shipper: null,
        formData: {}
      };
    },
    async restoreSTA() {
      try {
        await this.$store.dispatch('StandardTimeAnalysis/updateSTA', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            is_restore: true
          }
        });
        this.showSuccessNotification(this, 'Successfully restored STA');
        this.closeRestoreModal();
        this.cancelAllEdits();
        this.fetchSTA();
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to restore STA');
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchSTA()
      this.popUpSuccess = false
    },
    async saveEdit(row) {
      row.editForm.route_name.value = row.editForm.sta_route_details.map(item => item.value).join(' - ');
      
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(this, 'Some fields are empty, please fill them first')
        return
      }

      if (row.editForm.sta_route_details.length <= 1) {
        this.showFailedNotification(this, 'Route must have at least 2 locations')
        return
      }

      if (row.editForm.sta_route_details.length > 4) {
        this.showFailedNotification(this, 'Route cannot have more than 4 locations')
        return
      }
      
      try {
        const formData = {
          hp_id: row.editForm.hp_id.value,
          shipper: row.editForm.shipper.value,
          year: this.isAdding ? (this.filters?.year ?? new Date().getFullYear()) : row.year,
          route_name: row.editForm.route_name.value,
          sta_route_details: row.editForm.sta_route_details.map((route, index) => ({
            location_name: route.value,
            sequence_order: index + 1
          })),
          sta_details: this.staColumns.map(col => ({
            name: col.name,
            oee_id: col.oee_id,
            value: row.editForm.sta_details.find(d => d.name === col.name && d.oee_id === col.oee_id)?.value || row.editForm.sta_details.find(d => d.name === col.name)?.value ||  row.editForm.sta_details.find(d => d.oee_id === col.oee_id)?.value || 0 
          }))
        };
        this.isProcessing = true
        const isRestoreData = await this.checkRestoreData(formData);
        if (isRestoreData) {
          this.isProcessing = false
          return
        }
        if (this.isAdding) {
          delete formData.active
          await this.$store.dispatch('StandardTimeAnalysis/createSTA', formData);
          this.showSuccessNotification(this, 'Successfully created STA');
        } else {
          await this.$store.dispatch('StandardTimeAnalysis/updateSTA', {
            id: row.id,
            form: formData
          });
          this.showSuccessNotification(this, 'Successfully updated STA');
        }
        this.cancelAllEdits();
        this.fetchSTA();
        this.isProcessing = false
      } catch (e) {
        this.isProcessing = false
        const {error} = e
        this.showFailedNotification(this, error?.errors?.[0]?.message ?? (error?.message || e || 'Failed to save changes, please try again'))
      }
    },
    getStaValue(row, name, oeeId) {
      if (!row) return 0;
      const details = row.isEditing ? row.editForm.sta_details : row.sta_details;
      if (!details) return 0;
      const detail = details.find(d => d.name === name && d.oee_id === oeeId) || details.find(d => d.name === name) || details.find(d => d.oee_id === oeeId);
      return detail ? (detail.value !== undefined && detail.value !== null ? detail.value : 0) : 0;
    },
    updateStaValue(row, name, oeeId, value) {
      if (!row?.editForm?.sta_details) return;
      const index = row.editForm.sta_details.findIndex(d => d.name === name && d.oee_id === oeeId) || row.editForm.sta_details.findIndex(d => d.name === name) || row.editForm.sta_details.findIndex(d => d.oee_id === oeeId);
      if (index === -1) {
        row.editForm.sta_details.push({
          name,
          oee_id: oeeId,
          value: value || 0
        });
      } else {
        this.$set(row.editForm.sta_details[index], 'value', value || 0);
      }
      this.recalculateTotals(row);
    },
    recalculateTotals(row) {
      if (!row || !row.editForm || !row.editForm.sta_details) return;

      const details = row.editForm.sta_details;
      
      const totalTime = details
        .filter(detail => this.operationColumns.includes(detail.name))
        .reduce((sum, detail) => sum + (parseFloat(detail.value) || 0), 0);

      const totalDelay = details
        .filter(detail => this.delayColumns.includes(detail.name))
        .reduce((sum, detail) => sum + (parseFloat(detail.value) || 0), 0);
      
      const totalDowntime = details
        .filter(detail => this.downtimeColumns.includes(detail.name))
        .reduce((sum, detail) => sum + (parseFloat(detail.value) || 0), 0);

      const totalDelayTime = Number((totalDelay / 24).toFixed(2));
      const totalDowntimeTime = Number((totalDowntime / 24).toFixed(2));
      const totalOperatingTime = Number((totalTime / 24).toFixed(2));
      const totalBargeCycle = Number((totalOperatingTime + totalDelayTime + totalDowntimeTime).toFixed(2));

      this.$set(row, 'total_operating_time', totalOperatingTime);
      this.$set(row, 'total_delay_time', totalDelayTime);
      this.$set(row, 'total_downtime_time', totalDowntimeTime);
      this.$set(row, 'total_barge_cycle', totalBargeCycle);
    },
    showDuplicatePopup() {
      if (this.standardTimeData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }
      this.duplicatePopup.isShowChoose = true;
    },
    showDuplicateConfirmation() {
      if (!this.duplicatePopup.sourceYear || !this.duplicatePopup.targetYear) {
        this.showFailedNotification(this, 'Please select both source and target years')
        return
      }
      if (this.duplicatePopup.sourceYear === this.duplicatePopup.targetYear) {
        this.showFailedNotification(this, 'Source and target years cannot be the same')
        return
      }
      this.duplicatePopup.isShowChoose = false
      this.duplicatePopup.isShowConfirm = true
    },
    handleDuplicateClose() {
      this.duplicatePopup.isShowChoose = false
      this.duplicatePopup.isShowConfirm = false
      this.duplicatePopup.sourceYear = ''
      this.duplicatePopup.targetYear = ''
    },
    async handleDuplicate() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('StandardTimeAnalysis/duplicateSTA', {
          source_year: Number(this.duplicatePopup.sourceYear),
          target_year: Number(this.duplicatePopup.targetYear)
        });
        this.showSuccessNotification(this, 'Successfully duplicated STA');
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to duplicate STA');
      } finally {
        this.isProcessing = false;
      }
      this.handleDuplicateClose();
      this.fetchSTA();
      this.fetchYear();
    }
  }
}
</script>
  
<style scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

</style>
  
  